/**
 * Space Task Notification Client
 *
 * Browser helpers for routing Space task mutations through the unified
 * notification event intake.
 *
 * Primary responsibilities:
 * - Gate Space task notification event dispatch to browser paths
 * - Send normalized task mutation events to the notification API
 *
 * Dependencies:
 * - Space task notification event types
 * - Safe console logging
 */

import { type SpaceTaskNotificationEvent } from '@/lib/notifications/space-task-events'
import { safeConsole } from '@/lib/utils/safeConsole'

export function canDispatchSpaceTaskNotificationEvents() {
  return typeof window !== 'undefined'
}

export async function dispatchSpaceTaskNotificationEvents(
  events: SpaceTaskNotificationEvent[]
) {
  if (!canDispatchSpaceTaskNotificationEvents() || events.length === 0) {
    return
  }

  try {
    const response = await fetch('/api/notifications/space-task-events', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ events })
    })

    if (!response.ok) {
      const payload = (await response.json().catch(() => null)) as
        | { error?: string }
        | null

      throw new Error(
        payload?.error || 'Failed to dispatch Space task notifications'
      )
    }
  } catch (error) {
    safeConsole.error('Failed to dispatch Space task notifications', error)
  }
}
